import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Mail, User, Phone, ShieldCheck } from 'lucide-react';
import { useCountryConfig } from '../../hooks/useCountryConfig';

const PROVIDER_LABELS = { google: 'Google', microsoft: 'Microsoft' };

const inputStyle = { backgroundColor: "var(--lm-s1)", border: "1px solid #23252a", color: "var(--lm-ink)", borderRadius: "8px", padding: "10px 12px", fontSize: "14px", width: "100%", outline: "none", transition: "border-color 0.15s" };
const inputLockedStyle = { ...inputStyle, backgroundColor: "rgba(94,106,210,0.06)", borderColor: "rgba(94,106,210,0.3)", color: "var(--lm-ink-2)", cursor: "not-allowed" };
const inputErrorStyle = { ...inputStyle, borderColor: "#ef4444" };
const labelStyle = { display: "block", fontSize: "12px", fontWeight: "500", marginBottom: "6px", color: "var(--lm-ink-2)" };

// Top-level component (NOT redefined on each render) — an inline definition
// creates a new component type every render, so React remounts the <input>
// and the field loses focus after every keystroke.
const Field = ({ field, label, icon: Icon, type = "text", placeholder, dir = "auto", optional = false, locked = false, optionalLabel, value, error, onChange, onFocus, onBlur }) => (
  <div>
    <label htmlFor={`registration-${field}`} style={labelStyle}>
      <Icon className="w-3.5 h-3.5 inline ltr:mr-1.5 rtl:ml-1.5" style={{ color: "#5e6ad2" }} />
      {label}{!optional && " *"}
      {optional && <span className="ml-1" style={{ color: "var(--lm-ink-3)" }}>({optionalLabel})</span>}
    </label>
    <input
      id={`registration-${field}`}
      name={field}
      type={type}
      value={value}
      onChange={locked ? undefined : e => onChange(field, e.target.value)}
      readOnly={locked}
      placeholder={placeholder}
      dir={dir}
      aria-invalid={!!error}
      aria-describedby={error ? `registration-${field}-error` : undefined}
      style={locked ? inputLockedStyle : error ? inputErrorStyle : inputStyle}
      onFocus={locked ? undefined : onFocus}
      onBlur={locked ? undefined : e => onBlur(e, !!error)}
    />
    {error && <p id={`registration-${field}-error`} className="mt-1 text-xs" style={{ color: "#ef4444" }}>{error}</p>}
  </div>
);

const Step2AdminContact = ({ formData, loading, onNext, onPrevious, serverErrors = {}, onClearServerError, countryCode, socialPrefill }) => {
  const { t } = useTranslation();
  const { config } = useCountryConfig(countryCode || 'TN');
  const phonePlaceholder = config?.phone?.placeholder ?? t('registration.step2.phonePlaceholder');
  const baseDomain = process.env.REACT_APP_BASE_DOMAIN || 'mawered.com';
  const providerLabel = PROVIDER_LABELS[socialPrefill?.provider] || socialPrefill?.provider || '';

  const [data, setData] = useState({
    admin_email: formData.admin_email || '',
    admin_first_name: formData.admin_first_name || '',
    admin_last_name: formData.admin_last_name || '',
    admin_phone: formData.admin_phone || '',
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    if (serverErrors[field] && onClearServerError) onClearServerError(field);
  };

  const getFieldError = (field) => errors[field] || serverErrors[field];

  const validate = () => {
    const newErrors = {};
    if (!data.admin_email.trim()) newErrors.admin_email = t('registration.step2.validation.emailRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.admin_email)) newErrors.admin_email = t('registration.step2.validation.emailInvalid');
    if (!data.admin_first_name.trim()) newErrors.admin_first_name = t('registration.step2.validation.firstNameRequired');
    else if (data.admin_first_name.length < 2) newErrors.admin_first_name = t('registration.step2.validation.firstNameMinLength');
    if (!data.admin_last_name.trim()) newErrors.admin_last_name = t('registration.step2.validation.lastNameRequired');
    else if (data.admin_last_name.length < 2) newErrors.admin_last_name = t('registration.step2.validation.lastNameMinLength');
    if (data.admin_phone.trim()) { const digits = data.admin_phone.replace(/[\s+()-]/g, ''); if (digits.length < 8) newErrors.admin_phone = t('registration.step2.validation.phoneInvalid'); }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => { e.preventDefault(); if (validate()) onNext(data); };

  const onFocus = (e) => (e.target.style.borderColor = "#5e6ad2");
  const onBlur = (e, hasError) => (e.target.style.borderColor = hasError ? "#ef4444" : "var(--lm-border)");

  const fieldProps = (field) => ({
    field,
    value: data[field],
    error: getFieldError(field),
    onChange: handleChange,
    onFocus,
    onBlur,
    optionalLabel: t('common.optional'),
  });

  return (
    <div className="p-8">
      <div className="mb-7">
        <h2 className="text-xl font-semibold mb-1" style={{ color: "var(--lm-ink)", letterSpacing: "-0.4px" }}>{t('registration.step2.title')}</h2>
        <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>{t('registration.step2.subtitle')}</p>

        {/* Social pre-fill banner */}
        {socialPrefill && (
          <div className="mt-4 px-4 py-3 rounded-lg flex items-center gap-3"
            style={{ backgroundColor: "rgba(94,106,210,0.08)", border: "1px solid rgba(94,106,210,0.25)" }}>
            <ShieldCheck className="w-4 h-4 flex-shrink-0" style={{ color: "#828fff" }} />
            <p className="text-sm" style={{ color: "#828fff" }}>
              {t('registration.step2.socialPrefill', { provider: providerLabel })}
            </p>
          </div>
        )}

        {formData.subdomain && (
          <div className="mt-4 px-4 py-3 rounded-lg" style={{ backgroundColor: "rgba(94,106,210,0.1)", border: "1px solid rgba(94,106,210,0.3)" }}>
            <p className="text-sm" style={{ color: "#828fff" }}>
              <strong>{t('registration.step2.yourSubdomain')}</strong>{' '}
              <code className="font-mono" dir="ltr">{formData.subdomain}.{baseDomain}</code>
            </p>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <Field
          {...fieldProps('admin_email')}
          label={t('registration.step2.email')}
          icon={Mail}
          type="email"
          placeholder={t('registration.step2.emailPlaceholder')}
          dir="ltr"
          locked={!!socialPrefill}
        />
        {!socialPrefill && !getFieldError('admin_email') && (
          <p className="-mt-4 text-xs" style={{ color: "var(--lm-ink-3)" }}>{t('registration.step2.emailHelp')}</p>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field {...fieldProps('admin_first_name')} label={t('registration.step2.firstName')} icon={User} placeholder={t('registration.step2.firstNamePlaceholder')} />
          <Field {...fieldProps('admin_last_name')} label={t('registration.step2.lastName')} icon={User} placeholder={t('registration.step2.lastNamePlaceholder')} />
        </div>

        <Field {...fieldProps('admin_phone')} label={t('registration.step2.phone')} icon={Phone} type="tel" placeholder={phonePlaceholder} dir="ltr" optional />

        <div className="flex justify-between pt-5" style={{ borderTop: "1px solid #23252a" }}>
          <button type="button" onClick={onPrevious} disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: "var(--lm-s2)", color: "var(--lm-ink)", border: "1px solid #23252a" }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = "var(--lm-s3)")} onMouseLeave={e => (e.currentTarget.style.backgroundColor = "var(--lm-s2)")}>
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
            {t('common.back')}
          </button>
          <button type="submit" disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: "#5e6ad2", color: "#fff", opacity: loading ? 0.5 : 1 }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.backgroundColor = "#828fff"; }} onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#5e6ad2")}>
            {t('common.continue')}
            <ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default Step2AdminContact;
